import React, { useEffect, useState, useCallback } from "react";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { dateJP } from "@/lib/format";
import { DOC_CATEGORY } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { useLookups } from "@/context/LookupContext";
import { Loader2, Upload, Download, Trash2, FileText, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const CAT_LABEL = Object.fromEntries(DOC_CATEGORY.map((c) => [c.value, c.label]));

function fmtSize(b) {
  if (!b) return "-";
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1048576).toFixed(1)} MB`;
}

export default function Documents() {
  const { hasWrite } = useAuth();
  const { lookups, nameOf } = useLookups();
  const canWrite = hasWrite("documents");
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ title: "", category: "other", tags: "", property_id: "", note: "" });
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/documents"); setDocs(data); }
    catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!file) { toast.error("ファイルを選択してください"); return; }
    if (!form.title) { toast.error("タイトルを入力してください"); return; }
    const fd = new FormData();
    fd.append("file", file);
    Object.entries(form).forEach(([k, v]) => fd.append(k, v || ""));
    setSaving(true);
    try {
      await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("書類をアップロードしました");
      setOpen(false); setFile(null);
      setForm({ title: "", category: "other", tags: "", property_id: "", note: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "アップロードに失敗しました");
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/documents/${id}`); toast.success("削除しました"); load(); }
    catch { toast.error("削除に失敗しました"); }
  };

  const filtered = docs.filter((d) =>
    !q || d.title?.toLowerCase().includes(q.toLowerCase()) ||
    (d.tags || []).some((t) => t.toLowerCase().includes(q.toLowerCase())) ||
    CAT_LABEL[d.category]?.includes(q));

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="書類管理"
        subtitle="契約書・登記簿・見積書などを物件と関連付けて保存"
        actions={canWrite && (
          <Button onClick={() => setOpen(true)} className="bg-brand hover:bg-brand-hover text-white" data-testid="add-document">
            <Plus className="w-4 h-4 mr-1.5" />書類をアップロード
          </Button>
        )}
      />

      <div className="mb-4 max-w-sm">
        <Input placeholder="タイトル・タグ・カテゴリで検索..." value={q} onChange={(e) => setQ(e.target.value)} className="bg-white" data-testid="doc-search" />
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-border rounded-sm py-16 text-center text-muted-foreground">
          <FileText className="w-10 h-10 mx-auto mb-3 text-gray-300" />書類がありません
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((d) => (
            <div key={d.id} className="bg-white border border-border rounded-sm p-4 hover:border-brand/60 transition-colors" data-testid={`doc-${d.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="w-9 h-9 bg-brand-soft rounded-sm flex items-center justify-center shrink-0">
                  <FileText className="w-4.5 h-4.5 text-brand" />
                </div>
                <span className="text-xs bg-gray-100 px-2 py-0.5 rounded-sm">{CAT_LABEL[d.category] || d.category}</span>
              </div>
              <div className="font-medium text-sm mb-1 truncate">{d.title}</div>
              <div className="text-xs text-muted-foreground mb-2">
                {d.property_id ? nameOf("properties", d.property_id) : "未関連"} · {fmtSize(d.size)} · {dateJP(d.created_at)}
              </div>
              {(d.tags || []).length > 0 && (
                <div className="flex flex-wrap gap-1 mb-3">
                  {d.tags.map((t) => <span key={t} className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-sm">#{t}</span>)}
                </div>
              )}
              <div className="flex gap-2">
                <a href={`${fileUrl("/api/documents/" + d.id + "/download")}`} target="_blank" rel="noreferrer"
                  className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs border border-border rounded-sm py-1.5 hover:bg-gray-50" data-testid={`download-${d.id}`}>
                  <Download className="w-3.5 h-3.5" />ダウンロード
                </a>
                {canWrite && (
                  <button onClick={() => del(d.id)} className="px-2 border border-border rounded-sm hover:bg-red-50 hover:text-red-600 hover:border-red-200" data-testid={`del-doc-${d.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="document-dialog">
          <DialogHeader><DialogTitle>書類をアップロード</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">ファイル *</Label>
              <label className="flex items-center gap-2 px-3 h-10 border border-input rounded-sm cursor-pointer hover:bg-gray-50 text-sm">
                <Upload className="w-4 h-4" />{file ? file.name : "ファイルを選択"}
                <input type="file" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="doc-file" />
              </label>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">タイトル *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="doc-title" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-muted-foreground mb-1.5 block">カテゴリ</Label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full h-10 px-3 rounded-sm border border-input bg-white text-sm" data-testid="doc-category">
                  {DOC_CATEGORY.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-1.5 block">関連物件</Label>
                <select value={form.property_id} onChange={(e) => setForm({ ...form, property_id: e.target.value })}
                  className="w-full h-10 px-3 rounded-sm border border-input bg-white text-sm">
                  <option value="">なし</option>
                  {lookups.properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">タグ（カンマ区切り）</Label>
              <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="重要, 2026年" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>キャンセル</Button>
            <Button onClick={submit} disabled={saving} className="bg-brand hover:bg-brand-hover text-white" data-testid="doc-upload-save">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}アップロード
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
